import { UntypedFormGroup, UntypedFormArray } from '@angular/forms';
import { Source } from '../../shared/interfaces';

export enum AnalysesTypes {
  DNA = 'DNA',
  WGS = 'DNA-WGS',
  DNALifestyle = 'DNA Lifestyle',
  PRS = 'PRS',
  Blood = 'Blood',
  BloodSimple = 'Blood/Simple',
  Measurements = 'Measurement',
  MeasurementsSimple = 'Measurement/Simple',
  Questionnaire = 'Questionnaire',
  Habits = 'Habits',
  Healthscore = 'Healthscore',
  NIPT = 'NIPT', // TODO: Verify this
}

// TODO - this enum should be used when comparing it to type retrieved from url param...
// Why we also use enum AnalysesTypes? -because backend is still returning Capitalized strings in some responses... this should be fixed.
export enum AnalysesTypesLowercase {
  DNA = 'dna',
  PRS = 'prs',
  Blood = 'blood',
  Measurements = 'measurement',
  Habits = 'habits',
  Healthscore = 'healthscore',
  NIPT = 'nipt',
  Ancestry = 'ancestry',
  PhysicalTraits = 'physical-traits',
}

export enum AnalysesProcessingType {
  Qualitative = 'Qualitative',
  Simple = 'Simple',
  Ratio = 'Ratio',
  Correlation = 'Correlation',
}

export enum BloodPressureType {
  Diastolic = 'Diastolic',
  Systolic = 'Systolic',
}

export interface Type {
  id: string;
  type: string;
}

export interface AboutAnalysis {
  description: string;
  otherReferences: any[];
  scienceBehind: [{ link: string; title: string }];
  scientificSources: [{ link: string; title: string }];
}

export interface MeasurementTrackingResult {
  calculationId: string;
  generated: string;
  requestId: string;
  results: [{ value: number; unit: Unit }];
  semanticType: string;
}

export interface BloodTrackingResult {
  canAdd: boolean;
  canEdit: boolean;
  generated: string;
  id: string;
  processed: string;
  semanticType: string;
  unit: Unit;
  value: number;
  optimalLevel: OptimalLevel;
  rawMarker: RawMarker;
}

export interface RawMarker {
  id: string;
  value: number;
  unit: Unit;
}

export interface OptimalLevel {
  id: string;
  semanticType: string;
  from: number;
  to: number;
  isOptimal: boolean;
}

export interface HabitsTrackingReuslt {
  calculatedDate: string;
  calculationId: string;
  generatedDate: string;
  result: string;
  semanticType: string;
  value: number;
}

export type HistoryResult =
  | HabitsTrackingReuslt
  | BloodTrackingResult
  | MeasurementTrackingResult;

// ========================
// CONFIGURATIONS
// ========================

export interface BloodMarker {
  id: string;
  name: string;
  units: BloodMarkerUnit[];
}

export interface MeasurementMarker {
  id: string;
  shortName: string;
  longName: string;
  analysisType: string;
  measurementMetrics?: unknown[];
}

export interface HabitsQuestion {
  id: string;
  name: string;
  type: string;
  sequenceNumber: number;
  hint?: string; // TODO BE: update schema, to include this property
  description?: string; // Unused
  answers: {
    id: string;
    name: string;
    type: string;
    sequenceNumber: number;
    decimalPlaces?: number;
    min?: number;
    max?: number;
    answersToDisableOnSelection: unknown[];
    description?: string; // Unused
  }[];
}

export interface Unit {
  id: string;
  name: string;
  decimalPlaces: number;
  isVisibleResult?: boolean;
}

export interface BloodMarkerUnit extends Unit {
  isDefault: boolean;
  minValue: number;
  maxValue: number;
}

export interface MeasurementMarkerUnit extends Unit {
  isDefault: boolean;
  min: number;
  max: number;
}

export interface Configuration {
  id: string;
  shortName: string;
  longName: string;
  analysisType: string;
}

export interface BloodConfigurationOld extends Configuration {
  markers?: {
    id: string;
    name: string;
    units: BloodMarkerUnit[];
  }[];
  synonyms?: string[];
  processingType?: AnalysesProcessingType;
}

export interface HabitsConfiguration extends Configuration {
  questions?: HabitsQuestion[];
}

export interface MeasurementConfiguration extends Configuration {
  measurementMetrics?: MeasurementsResponseMetrics[];
}

export type MarkerUnit = BloodMarkerUnit | MeasurementMarkerUnit; // TODO - extend to other vertical types
export type AnalysisMarker = BloodMarker | HabitsQuestion | MeasurementMarker;
export type AnalysisConfiguration = MeasurementConfiguration &
  BloodConfigurationOld &
  HabitsConfiguration;

export interface AnalysisLevel {
  id: string;
  name: string;
  semanticType: string;
  from?: number;
  to?: number;
  isOptimal?: boolean;
  sequenceId?: number;
}

export interface AnalysisResult {
  analysisId?: string;
  shortName?: string;
  longName?: string;
  description?: string;
  analysisLevels?: AnalysisLevel[];
  generated?: string;
  processingType?: string;
  result?: {
    id?: string;
    calculationId: string;
    value?: number;
    resultValue?: string;
    canEdit?: boolean;
    canAdd?: boolean;
    markers?: Marker[];
    unit?: Unit;
    level?: {
      id?: string;
      name?: string;
      description?: string;
      semanticType?: string;
    };
    generated?: string;
    processed?: string;
    calculatedDate?: string;
    results?: any[]; //currently no interface for this
  };
}

export interface AnalysisPopulation {
  populationPercentage: number;
  semanticType: string;
}

export interface AnalysisResponse {
  analysisId: string;
  calculationId: string;
  shortName: string;
  longName: string;
  name: string;
  result: MeasurementResult[] | BloodResult | HabitsResult;
  semanticType: string;
  type: string;
  color?: string;
}

export interface MeasurementResult {
  result: number;
  unitName: string;
}

export interface BloodResult {
  id: string;
  value: number;
  semanticType: string;
  canEdit: true;
  canAdd: true;
  unit: Unit;
  generated?: string;
}

export type HabitsResult = string;

export interface Analysis {
  id: string;
  analysisId?: string;
  shortName?: string;
  longName?: string;
  pictureSmall?: string;
  pictureLarge?: string;
  type: string;
  name?: string;
  levels?: any[];
  result?: any;
  hasHistoryTracking: boolean;
  dataDistribution?: any;
  genes?: Gene[];
  influence?: any;
  codeName?: string;
  sources?: Source[];
  otherReferences?: Source[];
  genesVsEnvironment?: GenesVsEnvironment[];
  processingType?: string;
  facts?: any;
  didYouKnow?: any;
  description?: string;
  analysisLevels?: any[];
  genetics?: string;
  valueLabels?: any;
}

export interface Gene {
  gene?: string;
  description: string;
  genotype?: string;
  snpId: string;
  allele1: string;
  allele1SemanticType?: string;
  allele2: string;
  allele2SemanticType?: string;
}

export interface GenesVsEnvironment {
  title: string;
  description: string;
  ratio: string;
  genesRatio: number;
  environmentRatio: number;
}

export interface Result {
  id: string;
  reportId: string;
  type: string;
  metrics: Metric[];
  date: string;
}

export interface Metric {
  id: string;
  unit: {
    id: string;
    isDefault: boolean;
  };
  value: string;
  from: string;
  to: string;
}

export interface Next {
  id: string;
  name: string;
  domain: string;
  isProduct: boolean;
}

export interface IDateObject {
  year?: number;
  month?: number;
  day?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
}

export class MetricsExtensions {
  static toArray(
    metrics: UntypedFormArray,
    ignoreNullValues: boolean,
    analysisType: AnalysesTypesLowercase
  ): NewResultMetric[] {
    const resultArray = [];
    metrics.controls
      .filter(
        (element: UntypedFormGroup) => element.controls.value.value !== null
      )
      .forEach((element: UntypedFormGroup) => {
        if (ignoreNullValues && !element.controls.value.value) {
          return;
        }
        const {
          id,
          unit,
          units,
          type,
          qualitative,
          value,
          value1,
          from,
          to,
        } = element.controls;

        switch (analysisType) {
          case AnalysesTypesLowercase.Blood: {
            const currentUnit = units.value.find((u) => u.id === unit.value);

            const result: NewResultMetric = {
              markerId: id.value,
              unitId: unit?.value,
              value: parseFloat(value.value),
            };
            // Qualitative analyses do not require from/to
            if (qualitative.value) {
              const selected = qualitative.value.find(
                (x) => x.value === result.value
              );
              result.descriptive = selected.key;
              resultArray.push(result);
              break;
            }
            result.from = parseFloat(from.value);
            result.to = parseFloat(to.value);
            result.unitName = currentUnit.name; // TODO - this probably should not be part of post request, but is needed to be displayed in report summary
            result.isVisibleResult = currentUnit.isVisibleResult;

            resultArray.push(result);
            break;
          }
          case AnalysesTypesLowercase.Measurements:
            resultArray.push({
              id: id.value,
              unitId: unit?.value,
              value: value.value,
            });
            break;
          case AnalysesTypesLowercase.Habits:
            resultArray.push({
              id: id.value,
              answers:
                type.value === 'SingleChoice'
                  ? [
                      {
                        id: value.value, // selected option id
                        value: value1.value, // optional input value for selected option (for example cigarettes per day)
                      },
                    ]
                  : value.value,
            });
            break;
          case AnalysesTypesLowercase.DNA:
            alert('DNA Not imeplemented!');
            break;
        }
      });
    return resultArray;
  }
}

export interface DnaReportAnalysis {
  analysisId: string;
  longName: string;
  result?: string;
  semanticType?: string;
  shortName: string;
  type: AnalysesTypes;
}

export interface NewMeasurementsResult {
  measurements: Measurement[];
  generated: string;
}

export interface NewHabitsResult {
  questions: Question[];
  generated: string;
}

export interface Question {
  answers: { id: string; value?: string }[];
  id: string;
}

export interface QuestionnaireCreate {
  questions: Question[];
  generated?: string;
}

interface Measurement {
  id: string;
  value: string;
  unitId: string;
}

export interface NewBloodResult {
  inventoryItemId?: string;
  productInventoryItemId?: string;
  laboratoryName?: string;
  generated: string;
  markers: Marker[];
  process?: string;
  processBy?: string;
  processReason?: string;
}

export interface Marker {
  markerId: string;
  unitName?: string;
  unitId?: string;
  unit?: string;
  value: number;
  from?: number;
  to?: number;
  descriptive?: string;
  isVisibleResult?: boolean;
}

export type NewResultModel =
  | NewMeasurementsResult
  | NewBloodResult
  | NewHabitsResult;
type NewResultMetric = Measurement | Marker | Question;

export class ResultExtensions {
  static prepareBloodPayload(
    form: UntypedFormGroup,
    ignoreNullValues: boolean
  ) {
    const markers = MetricsExtensions.toArray(
      form.controls.metrics as UntypedFormArray,
      ignoreNullValues,
      AnalysesTypesLowercase.Blood
    );
    const generatedDate = this.objectToDate(
      form.controls.date.value
    ).toISOString();
    return {
      generated: generatedDate,
      markers: markers as Marker[],
    };
  }

  static prepareMeasurementPayload(
    form: UntypedFormGroup,
    ignoreNullValues: boolean
  ): NewMeasurementsResult {
    const generatedDate = this.objectToDate(
      form.controls.date.value
    ).toISOString();
    const measurements = MetricsExtensions.toArray(
      form.controls.metrics as UntypedFormArray,
      ignoreNullValues,
      AnalysesTypesLowercase.Measurements
    );
    return {
      measurements: measurements as Measurement[],
      generated: generatedDate,
    };
  }

  static prepareHabitsPayload(
    form: UntypedFormGroup,
    ignoreNullValues: boolean
  ): NewHabitsResult {
    const generatedDate = this.objectToDate(
      form.controls.date.value
    ).toISOString();
    const questions = MetricsExtensions.toArray(
      form.controls.metrics as UntypedFormArray,
      ignoreNullValues,
      AnalysesTypesLowercase.Habits
    );
    return {
      questions: questions as Question[],
      generated: generatedDate,
    };
  }

  static toResult(
    form: UntypedFormGroup,
    ignoreNullValues = false,
    type: AnalysesTypesLowercase
  ): NewResultModel {
    switch (type) {
      case AnalysesTypesLowercase.Blood:
        return this.prepareBloodPayload(form, ignoreNullValues);
      case AnalysesTypesLowercase.Measurements:
        return this.prepareMeasurementPayload(form, ignoreNullValues);
      case AnalysesTypesLowercase.Habits:
        return this.prepareHabitsPayload(form, ignoreNullValues);
    }
  }

  static objectToDate(obj: IDateObject): Date {
    const dateNow = new Date();
    return new Date(
      obj.year ? obj.year : dateNow.getFullYear(),
      obj.month ? obj.month - 1 : dateNow.getMonth(),
      obj.day ? obj.day : dateNow.getDate(),
      obj.hours ? obj.hours : dateNow.getHours(),
      obj.minutes ? obj.minutes : dateNow.getMinutes(),
      obj.seconds ? obj.seconds : dateNow.getSeconds(),
      obj.milliseconds ? obj.milliseconds : dateNow.getMilliseconds()
    );
  }

  static GetGenesRatio(genesVsEnvironment: GenesVsEnvironment): number {
    const ratioSplit = genesVsEnvironment.ratio.split('/');
    const ratio = parseInt(ratioSplit[0], 10);
    return Number.isNaN(ratio) ? 0 : ratio;
  }

  static GetEnvironmentRatio(genesVsEnvironment: GenesVsEnvironment): number {
    const ratioSplit = genesVsEnvironment.ratio.split('/');
    const ratio = parseInt(ratioSplit[1], 10);
    return Number.isNaN(ratio) ? 0 : ratio;
  }
}

export interface MeasurementsResponse {
  analysisType: string;
  id: string;
  longName: string;
  measurementMetrics: MeasurementsResponseMetrics[];
  shortName: string;
}

export interface MeasurementsResponseMetrics {
  id: string;
  name: string;
  units: MeasurementsResponseMetricsUnits[];
}

export interface MeasurementsResponseMetricsUnits {
  decimalPlaces: number;
  id: string;
  isDefault: boolean;
  max: number;
  min: number;
  name: string;
}

export interface Report {
  id: string;
  kitId?: string; // TODO update when added on API
  productId: string;
  type: string;
  status: string;
  title: string;
  description: string;
  claimed: string;
  received: string; // TODO update when added on API
  completed: string;
  date: string;
  image: string;
}

export interface StatisticsLevel {
  count: number;
  semanticType: string;
}

export interface KitInProgress {
  kitId: string;
  interactions: KitInProgressInteraction[];
}

export interface KitInProgressInteraction {
  interaction: KitInProgressInteractionType;
  interactedOn: string;
}

export enum KitInProgressInteractionType {
  CLAIMED = 'Claimed',
  UNCLAIMED = 'Unclaimed',
  RETURNED = 'Returned',
  SENT_TO_LABORATORY = 'SentToLaboratory',
}

// About
export interface Fact {
  description: string;
}

export interface ScientificSource {
  title: string;
  link: string;
}

export interface DNAAbout {
  type: string;
  description: string;
  facts: Fact[];
  scientificSources: ScientificSource[];
  genesVsEnvironment: any[];
}

export interface OtherReference {
  title: string;
  link: string;
}

export interface HealthscoreAbout {
  description: string;
  scientificSources: any[];
  scienceBehind: any[];
  otherReferences: OtherReference[];
}

export interface AnalysesResultDetails {
  analysisId: string;
  calculated: string;
  measurements?: AnalysesResultMeasurement[];
}

export interface AnalysesResultMeasurement {
  measurementId: string;
  unitId: string;
  from?: number;
  to?: number;
  value?: number;
}

// Blood catalogue

export interface CatalogueAnalysis {
  analysisId: string;
  shortName: string;
  longName: string;
  description: string;
}

export interface BloodCatalogue {
  id: string;
  sequenceNumber: number;
  name: string;
  image: string;
  analyses: CatalogueAnalysis[];
}

export interface ScienceBehind {
  link: string;
  title: string;
}

export interface CatalogueAnalysisAbout {
  description: string;
  scientificSources: ScientificSource[];
  scienceBehind: ScienceBehind[];
  otherReferences: any[];
}

export interface BloodPanel {
  id: string;
  name: string;
  image: string;
  analyses: BloodPanelAnalysis[];
}

export interface BloodPanelAnalysis {
  analysisId: string;
  shortName: string;
  longName: string;
  result: BloodPanelAnalysisResult;
}

export interface BloodPanelAnalysisResult {
  id: string;
  value: number;
  semanticType: string;
  canEdit: boolean;
  canAdd: boolean;
  unit: Unit;
  generated: string;
}

export interface HSScore {
  result: {
    average: number;
    level: {
      name: string;
      type: string;
      description: string;
      from: number;
      to: number;
    };
    value: number;
  };
  levels: any[];
}
